import React, { useEffect, useState, useRef } from 'react'
import { useWebsocket } from '../../../hooks/useWebsocket'
import {
  Grid,
  Segment,
  Header,
  Button,
  Dimmer,
  Loader,
  Message,
  Input,
  Form,
  Icon,
  List,
  Modal,
  Dropdown,
  Label,
} from 'semantic-ui-react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, PaperPlaneTilt, File, Upload, X } from 'phosphor-react'
import useAxios from '../../../lib/auth/useAxios'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import env from '../../../env'
import TakeoffActionPanel from '../../../components/TakeoffActionPanel'
import PDFViewer from '../../../components/PDFViewer/PDFViewer'
import PDFDrawingAnnotator from '../../../components/PDFDrawingAnnotator'
import MaterialsList from '../../../components/MaterialsList'
import MessageFormatter from '../../../components/MessageFormatter'
import './TakeoffView.css'

function TakeoffView() {
  const axios = useAxios()
  const navigate = useNavigate()
  const { takeoffId } = useParams()
  const user = useSelector((state) => state.user.user)
  const tenant = useSelector((state) => state?.globals?.tenant)
  const fileInputRef = useRef(null)

  const [takeoff, setTakeoff] = useState(null)
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState([])
  const [chatMessages, setChatMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [uploadingDocument, setUploadingDocument] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [documentModalOpen, setDocumentModalOpen] = useState(false)
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] =
    useState(null)
  const [aiTyping, setAiTyping] = useState(false)
  const [streamingMessage, setStreamingMessage] = useState('')
  const [leftWidth, setLeftWidth] = useState(0) // Left sidebar for documents - hidden by default
  const [rightWidth, setRightWidth] = useState(50) // Right sidebar for chat/actions - default to 50%
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState(null) // 'left' or 'right'
  const [showDocumentSidebar, setShowDocumentSidebar] = useState(false) // Control document sidebar visibility
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const [activeTab, setActiveTab] = useState('chat') // 'chat', 'actions', or 'materials'
  const [actionMode, setActionMode] = useState('plan') // For action panel mode
  const [drawingAnnotations, setDrawingAnnotations] = useState([]) // Drawing annotations for bounding boxes
  const [collapsedSections, setCollapsedSections] = useState({
    takeoffInfo: false,
    documentSummary: false,
    chatHistory: false,
    quickActions: true,
  })
  const chatEndRef = useRef(null)
  const containerRef = useRef(null)
  const { ws: websocket, wsOpen: connected } = useWebsocket()

  useEffect(() => {
    if (takeoffId) {
      loadTakeoffData()
    }
  }, [takeoffId])

  // Auto-select first document when documents load
  useEffect(() => {
    if (documents.length > 0 && !selectedDocumentForPreview) {
      // Use the previewDocument function to ensure full details are loaded
      previewDocument(documents[0])
      // Don't auto-show the sidebar - keep it hidden by default
      // User can click the button to show it
    }
  }, [documents, selectedDocumentForPreview])

  // Auto-scroll disabled per user request
  // useEffect(() => {
  //   scrollToBottom()
  // }, [chatMessages, streamingMessage])

  // Handle Option+T hotkey for details panel
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check for Option+T (Alt+T on Windows/Linux, Cmd+T on Mac)
      if ((event.altKey || event.metaKey) && event.key.toLowerCase() === 't') {
        event.preventDefault()
        setShowDetailsPanel((prev) => !prev)
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [])

  // Handle Shift+Tab for action mode toggle
  useEffect(() => {
    const handleModeToggle = (event) => {
      // Check for Shift+Tab
      if (event.shiftKey && event.key === 'Tab') {
        event.preventDefault()
        setActionMode((prev) => (prev === 'plan' ? 'execute' : 'plan'))
      }
    }

    document.addEventListener('keydown', handleModeToggle)
    return () => document.removeEventListener('keydown', handleModeToggle)
  }, [actionMode])

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current || !dragType) return

      const rect = containerRef.current.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const containerWidth = rect.width
      const mousePercentage = (mouseX / containerWidth) * 100

      if (dragType === 'left') {
        // Dragging left divider - adjust left panel width
        const constrainedWidth = Math.min(Math.max(mousePercentage, 15), 60)
        setLeftWidth(constrainedWidth)
      } else if (dragType === 'right') {
        // Dragging right divider - adjust right panel width
        const rightPercentage = 100 - mousePercentage
        // Allow right sidebar to grow up to 80% of the screen
        const constrainedWidth = Math.min(Math.max(rightPercentage, 15), 80)
        setRightWidth(constrainedWidth)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setDragType(null)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragType])

  const handleMouseDown = (type) => {
    setIsDragging(true)
    setDragType(type)
  }

  const resetLayout = () => {
    if (documents.length > 0 && showDocumentSidebar) {
      setLeftWidth(20) // Documents sidebar
      setRightWidth(30) // Chat sidebar
    } else {
      setLeftWidth(0) // No documents or sidebar hidden
      setRightWidth(50) // Default 50/50 split
    }
  }
  
  const toggleDocumentSidebar = () => {
    setShowDocumentSidebar(!showDocumentSidebar)
    if (!showDocumentSidebar) {
      // Showing the sidebar - set a reasonable width
      setLeftWidth(20)
    } else {
      // Hiding the sidebar
      setLeftWidth(0)
    }
  }

  const toggleSection = (sectionName) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }))
  }

  const toggleDetailsPanel = () => {
    setShowDetailsPanel((prev) => !prev)
  }

  useEffect(() => {
    console.log('WebSocket useEffect', { websocket, connected, takeoffId })

    if (websocket) {
      const handleWebSocketMessage = (event) => {
        console.log('WebSocket message received:', event.data)
        const data = JSON.parse(event.data)

        if (data.type === 'chat_chunk' && data.takeoff_id == takeoffId) {
          if (data.is_final) {
            // Final chunk - add complete AI message to chat and clear streaming
            setStreamingMessage((currentMessage) => {
              if (currentMessage) {
                const newAiMessage = {
                  id: data.message_id || Date.now(),
                  message: currentMessage,
                  message_type: 'ai',
                  created_at: new Date().toISOString(),
                  user: null,
                }
                setChatMessages((prev) => [...prev, newAiMessage])
              }
              return '' // Clear streaming message
            })
            setAiTyping(false)
          } else {
            // Streaming chunk - append to streaming message
            setStreamingMessage((prev) => prev + data.content)
          }
        } else if (
          data.type === 'chat_ai_thinking' &&
          data.takeoff_id == takeoffId
        ) {
          setAiTyping(true)
          setStreamingMessage('')
        } else if (data.type === 'chat_error' && data.takeoff_id == takeoffId) {
          toast.error(data.error)
          setAiTyping(false)
          setStreamingMessage('')
        } else if (
          data.type === 'chat_message_saved' &&
          data.takeoff_id == takeoffId
        ) {
          // User message was saved, add it to chat immediately
          const newUserMessage = {
            id: data.message_id || Date.now(),
            message: data.message,
            message_type: 'user',
            created_at: new Date().toISOString(),
            user: {
              first_name: 'You', // Placeholder - will be replaced with actual user data
              last_name: '',
            },
          }
          setChatMessages((prev) => [...prev, newUserMessage])
        }
      }

      websocket.addEventListener('message', handleWebSocketMessage)

      return () => {
        websocket.removeEventListener('message', handleWebSocketMessage)
      }
    }
  }, [websocket, takeoffId])

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadTakeoffData = async () => {
    setLoading(true)
    try {
      // Load takeoff details
      const takeoffResponse = await axios.get(`/main/takeoffs/${takeoffId}/`)
      setTakeoff(takeoffResponse.data)

      // Load documents
      const documentsResponse = await axios.get('/main/takeoff-documents/', {
        params: { takeoff_id: takeoffId },
      })
      setDocuments(documentsResponse.data.results || [])

      // Load chat messages
      const messagesResponse = await axios.get('/main/takeoff-chat-messages/', {
        params: { takeoff_id: takeoffId },
      })
      setChatMessages(messagesResponse.data.results || [])
      
      // Action run count removed - no longer needed with simplified tabs
      
      // Load drawing annotations for bounding box visualization
      await loadDrawingAnnotations(takeoffId)
    } catch (error) {
      console.error('Error loading takeoff data:', error)
      toast.error('Failed to load takeoff data')
    } finally {
      setLoading(false)
    }
  }

  const loadDrawingAnnotations = async (takeoffId) => {
    try {
      const response = await axios.get('/main/takeoff-drawings/', {
        params: { takeoff: takeoffId, page_size: 'infinite' },
      })
      
      const drawings = response.data.results || []
      
      // Transform TakeoffDrawing objects into annotation format for PDFDrawingAnnotator
      const annotations = drawings.map(drawing => ({
        drawing_id: drawing.label || drawing.drawing_id || 'Unknown',
        page_number: drawing.page_number || 1,
        bbox: drawing.bbox || [0, 0, 100, 100], // Default bbox if missing
        label: drawing.label || drawing.drawing_id || 'Drawing',
        drawing_type: drawing.drawing_type || 'Drawing',
        confidence: drawing.confidence || 1.0,
        color: '#FF0000', // Red color for detected drawings
        is_user_adjusted: false
      }))
      
      setDrawingAnnotations(annotations)
      console.log(`Loaded ${annotations.length} drawing annotations for takeoff ${takeoffId}`)
      
    } catch (error) {
      console.error('Error loading drawing annotations:', error)
      setDrawingAnnotations([]) // Clear annotations on error
    }
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setUploadingDocument(true)
    try {
      // Validate that we have required takeoff data
      if (!takeoff?.order?.id) {
        throw new Error(
          'Takeoff order information is missing. Please refresh the page and try again.'
        )
      }

      // First, upload the file as a document
      const formData = new FormData()
      formData.append('file', file)
      formData.append('document_name', file.name) // Correct field name
      formData.append('description', `Takeoff plan for ${takeoff.name}`)
      formData.append('visibility', 'Admin') // Required field - set to Admin for takeoff documents
      formData.append('order_id', takeoff.order?.id) // Required for DocumentViewSet to process the upload
      formData.append('document_type', 'Takeoff Plan') // Specify document type for better organization
      // Add required Anvil fields with generated values
      const timestamp = Date.now()
      const random = Math.random().toString(36).substr(2, 8)
      formData.append('anvil_eid', `takeoff_eid_${timestamp}_${random}`)
      formData.append(
        'anvil_doc_group_eid',
        `takeoff_group_${timestamp}_${random}`
      )
      formData.append('anvil_id', `takeoff_anvil_${timestamp}_${random}`)
      formData.append(
        'anvil_url',
        `https://anvil.com/takeoff/docs/${timestamp}_${random}`
      )

      const documentResponse = await axios.post('/main/documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Document upload successful

      // Check if document ID exists
      const documentId =
        documentResponse.data.id || documentResponse.data.data?.id
      if (!documentId) {
        throw new Error('Document ID not found in response')
      }

      // Then, associate it with the takeoff
      const payload = {
        takeoff: parseInt(takeoffId),
        document: documentId,
        name: file.name,
      }

      const createResponse = await axios.post(
        '/main/takeoff-documents/',
        payload
      )

      toast.success('Document uploaded successfully')
      await loadTakeoffData() // Reload to show the new document

      // Auto-open the uploaded document for preview
      console.log('Uploaded document data:', createResponse.data)
      setSelectedDocumentForPreview(createResponse.data)

      // Adjust layout to file browser mode
      setLeftWidth(20) // Documents sidebar
      setRightWidth(33) // Chat/actions sidebar (1/3 of screen)
    } catch (error) {
      console.error('Error uploading document:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      toast.error(
        `Failed to upload document: ${
          error.response?.data?.detail || error.message
        }`
      )
    } finally {
      setUploadingDocument(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const sendMessage = async () => {
    console.log('sendMessage called', {
      newMessage: newMessage.trim(),
      connected,
      websocket,
    })

    if (!newMessage.trim()) {
      console.log('No message to send')
      return
    }

    if (!connected) {
      console.log('WebSocket not connected')
      toast.error('Connection lost. Please refresh the page.')
      return
    }

    if (!websocket) {
      console.log('No websocket instance')
      toast.error('WebSocket not available. Please refresh the page.')
      return
    }

    setSendingMessage(true)
    try {
      // Prepare document context for AI
      const documentContext = await Promise.all(
        documents.map(async (doc) => {
          let documentDetails = doc.document_details
          const baseApiUrl = env.REACT_APP_API_URL || ''

          // If no document_details, try to fetch them for vision
          if (!documentDetails && doc.id) {
            try {
              const response = await axios.get(
                `/main/takeoff-documents/${doc.id}/`
              )
              documentDetails = response.data.document_details
            } catch (error) {
              console.warn(
                'Could not fetch full document details for vision:',
                doc.name
              )
            }
          }

          // Try to get file URL using the same logic as preview
          let fileUrl =
            documentDetails?.file ||
            doc.file ||
            documentDetails?.download_url ||
            doc.download_url

          // Make relative URLs absolute
          if (fileUrl && fileUrl.startsWith('/media/')) {
            fileUrl = `${baseApiUrl}${fileUrl}`
          } else if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${baseApiUrl}/${fileUrl.replace(/^\/+/, '')}`
          }

          // Fallback to download endpoint if no direct file URL
          if (!fileUrl && documentDetails?.id) {
            fileUrl = `${baseApiUrl}/main/download-document/?document_id=${documentDetails.id}`
          }

          // Extract file extension from filename if not available in document_details
          const fileName = doc.name || documentDetails?.document_name || ''
          const fileExtension =
            documentDetails?.document_extension ||
            fileName.split('.').pop()?.toLowerCase() ||
            'unknown'

          const docContext = {
            id: doc.id,
            name: fileName,
            type: fileExtension,
            uploaded_date: doc.created_at,
            description: doc.description || `Document for ${takeoff.name}`,
            file: fileUrl, // Include the actual file URL for AI vision
          }

          console.log('Document context for AI vision:', docContext)
          console.log('Document details available:', !!documentDetails)
          console.log('Generated file URL:', fileUrl)

          return docContext
        })
      )

      // Send message via WebSocket for real-time AI response
      const chatMessage = {
        action: 'chat',
        data: {
          message: newMessage.trim(),
          takeoff_id: parseInt(takeoffId),
          message_type: 'user',
          // Include document context automatically
          context: {
            takeoff: {
              id: takeoff.id,
              name: takeoff.name,
              order_number: takeoff.order?.order_number,
              created_by: `${takeoff.created_by?.first_name} ${takeoff.created_by?.last_name}`,
              created_at: takeoff.created_at,
            },
            documents: documentContext,
            document_count: documents.length,
            selected_document: selectedDocumentForPreview
              ? {
                  id: selectedDocumentForPreview.id,
                  name:
                    selectedDocumentForPreview.name ||
                    selectedDocumentForPreview.document_details?.document_name,
                  type: selectedDocumentForPreview.document_details
                    ?.document_extension,
                }
              : null,
          },
        },
      }

      console.log('Sending WebSocket message:', chatMessage)
      websocket.send(JSON.stringify(chatMessage))
      setNewMessage('')
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error('Failed to send message: ' + error.message)
    } finally {
      setSendingMessage(false)
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendMessage()
    }
  }

  const viewDocument = (document) => {
    setSelectedDocument(document)
    setDocumentModalOpen(true)
  }

  const previewDocument = async (document) => {
    // If the document already has full details, use it directly
    if (document.document_details) {
      setSelectedDocumentForPreview(document)
      return
    }

    // Otherwise, fetch the full document details
    try {
      const response = await axios.get(
        `/main/takeoff-documents/${document.id}/`
      )
      setSelectedDocumentForPreview(response.data)
    } catch (error) {
      console.error('Error fetching full document details:', error)
      // Fallback to using the light document data
      setSelectedDocumentForPreview(document)
      toast.error('Could not load full document details')
    }
  }

  const deleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`/main/takeoff-documents/${documentId}/`)
        toast.success('Document deleted successfully')
        loadTakeoffData()
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Failed to delete document')
      }
    }
  }

  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000,
        }}
      >
        <Loader active size="large" content="Loading takeoff..." />
      </div>
    )
  }

  if (!takeoff) {
    return (
      <Message negative>
        <Message.Header>Takeoff not found</Message.Header>
        <p>The requested takeoff could not be found.</p>
      </Message>
    )
  }

  return (
    <div className="takeoff-view">
      <div className="takeoff-header">
        <div className="takeoff-header-left">
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate(-1)}
            size="small"
            basic
          />
          <div className="takeoff-header-info">
            <Header as="h2">
              {takeoff.name}
            </Header>
            <Header.Subheader as="span" className="sub header">
              Order #{takeoff.order?.order_number} | Created by{' '}
              {takeoff.created_by?.first_name} {takeoff.created_by?.last_name}
            </Header.Subheader>
          </div>
        </div>

        <Button
          primary
          content="Add Document"
          loading={uploadingDocument}
          onClick={() => fileInputRef.current?.click()}
          style={{
            '--primaryColor': tenant?.deployment_primary_color,
            '--secondaryButtonColor': env.REACT_APP_SECONDARY_BUTTON_COLOR,
            border: 'none',
          }}
        />

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
          accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
        />
      </div>

      <div
        ref={containerRef}
        className="file-browser-layout"
        style={{
          display: 'flex',
          height: 'calc(100vh - 120px)',
          minHeight: '600px',
          position: 'relative',
        }}
      >
        {/* Documents Sidebar - Only visible when showDocumentSidebar is true */}
        {documents.length > 0 && showDocumentSidebar && (
          <div
            style={{
              width: `${leftWidth}%`,
              minWidth: '250px',
              maxWidth: '400px',
            }}
          >
            <Segment
              className="documents-sidebar"
              style={{ height: '100%', overflow: 'auto' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                }}
              >
                <Header as="h4" style={{ margin: 0, fontSize: '14px' }}>
                  <Icon name="file" />
                  Documents ({documents.length})
                </Header>
                <Button
                  size="mini"
                  icon="expand arrows alternate"
                  onClick={resetLayout}
                  basic
                  title="Reset Layout"
                />
              </div>

              <div className="documents-list" style={{ fontSize: '13px' }}>
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className={`document-item-compact ${
                      selectedDocumentForPreview?.id === doc.id
                        ? 'selected'
                        : ''
                    }`}
                    style={{
                      padding: '10px',
                      marginBottom: '8px',
                      border:
                        selectedDocumentForPreview?.id === doc.id
                          ? '2px solid #2185d0'
                          : '1px solid #e0e0e0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor:
                        selectedDocumentForPreview?.id === doc.id
                          ? '#f0f8ff'
                          : '#fff',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => previewDocument(doc)}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '6px',
                      }}
                    >
                      <Icon
                        name="file"
                        size="small"
                        style={{ marginRight: '8px' }}
                      />
                      <div
                        style={{
                          flex: 1,
                          fontWeight: '500',
                          fontSize: '12px',
                          lineHeight: '1.2',
                        }}
                      >
                        {doc.name || doc.document_details?.document_name}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#666',
                        marginBottom: '6px',
                      }}
                    >
                      {new Date(doc.created_at).toLocaleDateString()}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Button
                        size="mini"
                        icon="expand"
                        content="Modal"
                        onClick={(e) => {
                          e.stopPropagation()
                          viewDocument(doc)
                        }}
                        style={{ fontSize: '10px', padding: '4px 8px' }}
                      />
                      <Button
                        size="mini"
                        icon="trash"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteDocument(doc.id)
                        }}
                        style={{ fontSize: '10px', padding: '4px 8px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Segment>
          </div>
        )}

        {/* Resizable Divider - Only show when sidebar is visible */}
        {documents.length > 0 && showDocumentSidebar && (
          <div
            onMouseDown={() => handleMouseDown('left')}
            style={{
              width: '8px',
              backgroundColor:
                isDragging && dragType === 'left' ? '#2185d0' : '#e1e1e1',
              cursor: 'col-resize',
              position: 'relative',
              zIndex: 10,
              transition: isDragging ? 'none' : 'background-color 0.2s',
              borderLeft: '1px solid #d4d4d5',
              borderRight: '1px solid #d4d4d5',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '3px',
                height: '30px',
                backgroundColor:
                  isDragging && dragType === 'left' ? '#fff' : '#999',
                borderRadius: '2px',
                transition: isDragging ? 'none' : 'background-color 0.2s',
              }}
            />
          </div>
        )}

        {/* Main Content Area - Document Preview */}
        <div
          style={{
            width:
              documents.length > 0 && showDocumentSidebar
                ? `${100 - leftWidth - rightWidth}%`
                : `${100 - rightWidth}%`,
            minWidth: '300px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%', // Ensure full height
            overflow: 'hidden', // Prevent this container from scrolling
          }}
        >
          {selectedDocumentForPreview ? (
            // Document Preview Mode
            <Segment
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                margin: '0 8px 0 0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px',
                  borderBottom: '1px solid #e0e0e0',
                  paddingBottom: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {documents.length > 0 && (
                    <Button
                      size="small"
                      icon={showDocumentSidebar ? 'sidebar' : 'list'}
                      content={showDocumentSidebar ? 'Hide Sidebar' : 'Show Sidebar'}
                      onClick={toggleDocumentSidebar}
                      basic
                    />
                  )}
                  <Header as="h3" style={{ margin: 0 }}>
                    <Icon name="file" />
                    {selectedDocumentForPreview.name ||
                      selectedDocumentForPreview.document_details?.document_name}
                  </Header>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    size="small"
                    icon="expand arrows alternate"
                    content="Reset Layout"
                    onClick={resetLayout}
                    basic
                  />
                  <Button
                    size="small"
                    icon="external"
                    content="Open in Modal"
                    onClick={() => viewDocument(selectedDocumentForPreview)}
                    basic
                  />
                </div>
              </div>

              <div
                className="document-container"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  overflow: 'auto', // Allow document content to scroll independently
                  maxHeight: '100%', // Ensure it doesn't exceed container height
                  height: '100%', // Take full available height
                  position: 'relative',
                }}
              >
                {(() => {
                  // Try multiple ways to get the file URL
                  const documentDetails =
                    selectedDocumentForPreview.document_details
                  const baseApiUrl = env.REACT_APP_API_URL || ''

                  console.log(
                    'Preview attempt - Document details:',
                    documentDetails
                  )
                  console.log(
                    'Preview attempt - Full document:',
                    selectedDocumentForPreview
                  )
                  console.log('Preview attempt - Base API URL:', baseApiUrl)

                  // Try to get file URL from various sources
                  let fileUrl =
                    documentDetails?.file ||
                    selectedDocumentForPreview.file ||
                    documentDetails?.download_url ||
                    selectedDocumentForPreview.download_url

                  // If we have a relative file path, make it absolute
                  if (fileUrl && fileUrl.startsWith('/media/')) {
                    fileUrl = `${baseApiUrl}${fileUrl}`
                  } else if (fileUrl && !fileUrl.startsWith('http')) {
                    // Handle other relative paths
                    fileUrl = `${baseApiUrl}/${fileUrl.replace(/^\/+/, '')}`
                  }

                  // Fallback to download endpoint if no direct file URL
                  if (!fileUrl && documentDetails?.id) {
                    fileUrl = `${baseApiUrl}/main/download-document/?document_id=${documentDetails.id}`
                  }

                  console.log('Preview file URL:', fileUrl)

                  if (fileUrl) {
                    // Determine file type for proper display
                    const fileName =
                      selectedDocumentForPreview.name ||
                      selectedDocumentForPreview.document_details
                        ?.document_name ||
                      ''
                    let fileExtension =
                      fileName.split('.').pop()?.toLowerCase() ||
                      selectedDocumentForPreview.document_details?.document_extension?.toLowerCase() ||
                      documentDetails?.document_extension?.toLowerCase()

                    // If still no extension, try to extract from file URL
                    if (!fileExtension && fileUrl) {
                      const urlPath = fileUrl.split('?')[0] // Remove query params
                      const urlFileName = urlPath.split('/').pop()
                      if (urlFileName && urlFileName.includes('.')) {
                        fileExtension = urlFileName
                          .split('.')
                          .pop()
                          .toLowerCase()
                      }
                    }
                    const isImage = [
                      'png',
                      'jpg',
                      'jpeg',
                      'gif',
                      'webp',
                      'svg',
                    ].includes(fileExtension)
                    const isPdf = fileExtension === 'pdf'

                    if (isImage) {
                      return (
                        <img
                          src={fileUrl}
                          alt={fileName}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', fileUrl)
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                      )
                    } else if (isPdf) {
                      return (
                        <div style={{ 
                          width: '100%', 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}>
                          <PDFViewer
                            documentUrl={fileUrl}
                            documentName={fileName}
                            onError={(error) => {
                              console.error('PDF loading error:', error)
                              toast.error('Failed to load PDF document')
                            }}
                          />
                          {/* Annotation overlay - will be implemented next */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            pointerEvents: 'none',
                            zIndex: 10
                          }}>
                            {/* Drawing annotations count display */}
                            {drawingAnnotations.length > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                color: 'white',
                                padding: '5px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                {drawingAnnotations.length} drawings detected
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    } else {
                      return (
                        <iframe
                          src={fileUrl}
                          width="100%"
                          height="100%"
                          title="Document Preview"
                          style={{ 
                            border: 'none', 
                            borderRadius: '8px',
                            flex: 1, // Take available height in flex container
                            minHeight: '400px' // Reduce minimum height for better fit
                          }}
                        />
                      )
                    }
                  } else {
                    return (
                      <div style={{ textAlign: 'center', color: '#666' }}>
                        <Icon
                          name="file"
                          size="huge"
                          style={{ opacity: 0.3, marginBottom: '20px' }}
                        />
                        <div style={{ fontSize: '18px', marginBottom: '10px' }}>
                          Document Preview Not Available
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                          Unable to generate file URL for this document.
                        </div>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#999',
                            marginBottom: '10px',
                          }}
                        >
                          File:{' '}
                          {selectedDocumentForPreview.name ||
                            selectedDocumentForPreview.document_details
                              ?.document_name}
                          <br />
                          Type:{' '}
                          {(() => {
                            const fileName =
                              selectedDocumentForPreview.name ||
                              selectedDocumentForPreview.document_details
                                ?.document_name ||
                              ''
                            return (
                              fileName.split('.').pop()?.toLowerCase() ||
                              selectedDocumentForPreview.document_details?.document_extension?.toLowerCase() ||
                              'Unknown'
                            )
                          })()}
                          <br />
                          Uploaded:{' '}
                          {new Date(
                            selectedDocumentForPreview.created_at
                          ).toLocaleDateString()}
                        </div>
                        <Button
                          style={{ marginTop: '15px' }}
                          primary
                          icon="external"
                          content="Open in New Window"
                          onClick={() => window.open(fileUrl || '#', '_blank')}
                        />
                      </div>
                    )
                  }
                })()}
              </div>
            </Segment>
          ) : (
            // No Document Selected - Show Welcome Message
            <Segment
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 8px 0 0',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                {documents.length > 0 && !showDocumentSidebar && (
                  <Button
                    size="medium"
                    icon="list"
                    content="Show Document Sidebar"
                    onClick={toggleDocumentSidebar}
                    basic
                    style={{ position: 'absolute', top: '20px', left: '20px' }}
                  />
                )}
                <Icon
                  name="file text"
                  size="huge"
                  style={{ opacity: 0.3, marginBottom: '20px' }}
                />
                <Header as="h3" style={{ color: '#666' }}>
                  Select a Document to Preview
                </Header>
                <p style={{ color: '#888', marginBottom: '20px' }}>
                  {documents.length > 0 && !showDocumentSidebar 
                    ? 'Click "Show Document Sidebar" to see your documents.'
                    : 'Click on any document from the left sidebar to view it here.'}
                </p>
                {documents.length === 0 && (
                  <Button
                    primary
                    size="large"
                    icon="upload"
                    content="Upload Your First Document"
                    loading={uploadingDocument}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      '--primaryColor': tenant?.deployment_primary_color,
                      '--secondaryButtonColor':
                        env.REACT_APP_SECONDARY_BUTTON_COLOR,
                      border: 'none',
                    }}
                  />
                )}
              </div>
            </Segment>
          )}
        </div>

        {/* Right Resizable Divider */}
        <div
          onMouseDown={() => handleMouseDown('right')}
          style={{
            width: '8px',
            backgroundColor:
              isDragging && dragType === 'right' ? '#2185d0' : '#e1e1e1',
            cursor: 'col-resize',
            position: 'relative',
            zIndex: 10,
            transition: isDragging ? 'none' : 'background-color 0.2s',
            borderLeft: '1px solid #d4d4d5',
            borderRight: '1px solid #d4d4d5',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '3px',
              height: '30px',
              backgroundColor:
                isDragging && dragType === 'right' ? '#fff' : '#999',
              borderRadius: '2px',
              transition: isDragging ? 'none' : 'background-color 0.2s',
            }}
          />
        </div>

        {/* Right Sidebar - Chat & Actions */}
        <div
          style={{
            width: `${rightWidth}%`,
            minWidth: '400px',
            height: '100%', // Ensure full height
            display: 'flex',
            flexDirection: 'column',
            // Removed maxWidth to allow sidebar to grow larger
          }}
        >
          <Segment
            className="chat-section"
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* Tab Navigation */}
            <div
              style={{
                borderBottom: '2px solid #e0e0e0',
                marginBottom: '20px',
              }}
            >
              <Button.Group fluid size="small">
                <Button
                  active={activeTab === 'chat'}
                  onClick={() => setActiveTab('chat')}
                >
                  <Icon name="comments" />
                  Chat
                </Button>
                <Button
                  active={activeTab === 'actions'}
                  onClick={() => setActiveTab('actions')}
                >
                  <Icon name="magic" />
                  Actions
                  <Label
                    color={actionMode === 'plan' ? 'blue' : 'green'}
                    size="mini"
                    style={{ marginLeft: '8px' }}
                  >
                    {actionMode}
                  </Label>
                </Button>
                <Button
                  active={activeTab === 'materials'}
                  onClick={() => setActiveTab('materials')}
                >
                  <Icon name="list" />
                  Materials
                </Button>
              </Button.Group>
            </div>

            {/* Header Section */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <Header as="h3" style={{ margin: 0 }}>
                <Icon name={
                  activeTab === 'materials' ? 'list' : 
                  activeTab === 'actions' ? 'magic' : 
                  'comments'
                } />
                {activeTab === 'materials' ? 'Materials List' :
                 activeTab === 'actions' ? 'Takeoff Actions' : 
                 'AI Assistant Chat'}
                {activeTab === 'chat' && (
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 'normal',
                      color: connected ? 'green' : 'red',
                    }}
                  >
                    {connected ? '● Connected' : '● Disconnected'}
                  </div>
                )}
              </Header>
              <div style={{ display: 'flex', gap: '8px' }}>
                {activeTab === 'chat' && (
                  <Button
                    size="mini"
                    icon={showDetailsPanel ? 'eye slash' : 'eye'}
                    content={showDetailsPanel ? 'Hide Details' : 'Show Details'}
                    onClick={toggleDetailsPanel}
                    basic
                    title="Toggle details panel (Option+T)"
                  />
                )}
                <Button
                  size="mini"
                  icon="expand arrows alternate"
                  content="Reset"
                  onClick={resetLayout}
                  basic
                />
              </div>
            </div>

            {/* Content Area */}
            {activeTab === 'materials' ? (
              // Materials List Panel
              <MaterialsList
                takeoffId={takeoffId}
                primaryColor={tenant?.deployment_primary_color}
                secondaryColor={tenant?.deployment_secondary_color}
              />
            ) : activeTab === 'actions' ? (
              // Action Panel
              <TakeoffActionPanel
                websocket={websocket}
                takeoffId={takeoffId}
                documents={documents}
                selectedDocument={selectedDocumentForPreview}
                onSendMessage={(message, role, isChunk) => {
                  // Handle messages from action panel if needed
                  if (!isChunk) {
                    setChatMessages((prev) => [
                      ...prev,
                      {
                        id: Date.now(),
                        message: message,
                        message_type: role === 'user' ? 'user' : 'ai',
                        created_at: new Date().toISOString(),
                      },
                    ])
                  }
                }}
                mode={actionMode}
                onModeChange={setActionMode}
                primaryColor={tenant?.deployment_primary_color}
                secondaryColor={tenant?.deployment_secondary_color}
              />
            ) : (
              // Chat Interface
              <>
                <div
                  className="chat-messages"
                  style={{ flex: 1, overflow: 'auto', marginBottom: '15px' }}
                >
                  {chatMessages.length === 0 &&
                  !aiTyping &&
                  !streamingMessage ? (
                    <Message
                      info
                      header="Start a conversation"
                      content="Ask the AI assistant questions about your takeoff plans, measurements, or construction details."
                    />
                  ) : (
                    <>
                      {chatMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`message ${
                            message.message_type === 'user'
                              ? 'user-message'
                              : 'ai-message'
                          }`}
                        >
                          <div className="message-content">
                            <div className="message-text">
                              {message.message_type === 'ai' ? (
                                <MessageFormatter content={message.message} />
                              ) : (
                                message.message
                              )}
                            </div>
                            <div className="message-meta">
                              {message.message_type === 'user'
                                ? `${message.user?.first_name} ${message.user?.last_name}`
                                : 'AI Assistant'}{' '}
                              •{' '}
                              {new Date(
                                message.created_at
                              ).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* AI Typing Indicator */}
                      {aiTyping && (
                        <div className="message ai-message">
                          <div className="message-content typing-indicator">
                            <div className="typing-dots">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                            <div className="message-meta">
                              AI Assistant is thinking...
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Streaming Message */}
                      {streamingMessage && (
                        <div className="message ai-message">
                          <div className="message-content streaming-message">
                            <div className="message-text">
                              {/* <MessageFormatter content={streamingMessage} isStreaming={true} /> */}
                              {streamingMessage}
                              <span className="cursor">|</span>
                            </div>
                            <div className="message-meta">
                              AI Assistant • typing...
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Claude Code-inspired Details Panel */}
                {showDetailsPanel && (
                  <div
                    className="details-panel"
                    style={{
                      marginBottom: '15px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    {/* Takeoff Information Section */}
                    <div className="details-section">
                      <div
                        className="details-section-header"
                        onClick={() => toggleSection('takeoffInfo')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: '#f5f5f5',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <Icon
                          name={
                            collapsedSections.takeoffInfo
                              ? 'chevron right'
                              : 'chevron down'
                          }
                        />
                        Takeoff Information
                      </div>
                      {!collapsedSections.takeoffInfo && (
                        <div style={{ padding: '12px 16px', fontSize: '13px' }}>
                          <div>
                            <strong>Order:</strong> #
                            {takeoff.order?.order_number}
                          </div>
                          <div>
                            <strong>Created by:</strong>{' '}
                            {takeoff.created_by?.first_name}{' '}
                            {takeoff.created_by?.last_name}
                          </div>
                          <div>
                            <strong>Created:</strong>{' '}
                            {new Date(takeoff.created_at).toLocaleDateString()}
                          </div>
                          <div>
                            <strong>Status:</strong>{' '}
                            {takeoff.status || 'In Progress'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Document Summary Section */}
                    <div className="details-section">
                      <div
                        className="details-section-header"
                        onClick={() => toggleSection('documentSummary')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: '#f5f5f5',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <Icon
                          name={
                            collapsedSections.documentSummary
                              ? 'chevron right'
                              : 'chevron down'
                          }
                        />
                        Documents ({documents.length})
                      </div>
                      {!collapsedSections.documentSummary && (
                        <div style={{ padding: '12px 16px', fontSize: '13px' }}>
                          {documents.length === 0 ? (
                            <div style={{ color: '#666', fontStyle: 'italic' }}>
                              No documents uploaded yet
                            </div>
                          ) : (
                            documents.map((doc) => (
                              <div
                                key={doc.id}
                                style={{
                                  marginBottom: '8px',
                                  padding: '6px',
                                  backgroundColor: '#fff',
                                  borderRadius: '4px',
                                }}
                              >
                                <div style={{ fontWeight: '500' }}>
                                  {doc.name || doc.document_details?.name}
                                </div>
                                <div
                                  style={{ color: '#666', fontSize: '12px' }}
                                >
                                  Uploaded{' '}
                                  {new Date(
                                    doc.created_at
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Chat History Section */}
                    <div className="details-section">
                      <div
                        className="details-section-header"
                        onClick={() => toggleSection('chatHistory')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: '#f5f5f5',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <Icon
                          name={
                            collapsedSections.chatHistory
                              ? 'chevron right'
                              : 'chevron down'
                          }
                        />
                        Chat Summary ({chatMessages.length} messages)
                      </div>
                      {!collapsedSections.chatHistory && (
                        <div style={{ padding: '12px 16px', fontSize: '13px' }}>
                          {chatMessages.length === 0 ? (
                            <div style={{ color: '#666', fontStyle: 'italic' }}>
                              No messages yet
                            </div>
                          ) : (
                            <div>
                              <div>
                                First message:{' '}
                                {new Date(
                                  chatMessages[0]?.created_at
                                ).toLocaleString()}
                              </div>
                              <div>
                                Last message:{' '}
                                {new Date(
                                  chatMessages[
                                    chatMessages.length - 1
                                  ]?.created_at
                                ).toLocaleString()}
                              </div>
                              <div>
                                User messages:{' '}
                                {
                                  chatMessages.filter(
                                    (m) => m.message_type === 'user'
                                  ).length
                                }
                              </div>
                              <div>
                                AI responses:{' '}
                                {
                                  chatMessages.filter(
                                    (m) => m.message_type === 'ai'
                                  ).length
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quick Actions Section */}
                    <div className="details-section">
                      <div
                        className="details-section-header"
                        onClick={() => toggleSection('quickActions')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          backgroundColor: '#f5f5f5',
                          fontSize: '14px',
                          fontWeight: '600',
                        }}
                      >
                        <Icon
                          name={
                            collapsedSections.quickActions
                              ? 'chevron right'
                              : 'chevron down'
                          }
                        />
                        Quick Actions
                      </div>
                      {!collapsedSections.quickActions && (
                        <div style={{ padding: '12px 16px' }}>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}
                          >
                            <Button
                              size="tiny"
                              content="Ask about measurements"
                              onClick={() =>
                                setNewMessage(
                                  'What are the key measurements I should focus on for this takeoff?'
                                )
                              }
                              basic
                            />
                            <Button
                              size="tiny"
                              content="Request material estimate"
                              onClick={() =>
                                setNewMessage(
                                  'Can you help me estimate the materials needed for this project?'
                                )
                              }
                              basic
                            />
                            <Button
                              size="tiny"
                              content="Review document"
                              onClick={() =>
                                setNewMessage(
                                  'Please review the uploaded documents and summarize the key details.'
                                )
                              }
                              basic
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Hotkey hint */}
                    <div
                      style={{
                        padding: '8px 16px',
                        fontSize: '11px',
                        color: '#666',
                        textAlign: 'center',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      Press{' '}
                      <kbd
                        style={{
                          padding: '2px 6px',
                          backgroundColor: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                        }}
                      >
                        Option
                      </kbd>{' '}
                      +{' '}
                      <kbd
                        style={{
                          padding: '2px 6px',
                          backgroundColor: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                        }}
                      >
                        T
                      </kbd>{' '}
                      to toggle this panel
                    </div>
                  </div>
                )}

                <div className="chat-input" style={{ flexShrink: 0 }}>
                  <Input
                    fluid
                    placeholder="Ask the AI assistant about your takeoff..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    action={{
                      icon: <PaperPlaneTilt size={16} />,
                      onClick: sendMessage,
                      loading: sendingMessage,
                      disabled: !newMessage.trim(),
                    }}
                  />
                </div>
              </>
            )}
          </Segment>
        </div>
      </div>

      {/* Document Viewer Modal */}
      <Modal
        open={documentModalOpen}
        onClose={() => setDocumentModalOpen(false)}
        size="large"
      >
        <Modal.Header>
          {selectedDocument?.name || selectedDocument?.document_details?.name}
          <Button
            floated="right"
            icon={<X size={16} />}
            onClick={() => setDocumentModalOpen(false)}
          />
        </Modal.Header>
        <Modal.Content>
          {(() => {
            // Try multiple possible file URL fields
            const documentDetails = selectedDocument?.document_details
            const fileUrl =
              documentDetails?.file ||
              selectedDocument?.file ||
              documentDetails?.download_url ||
              selectedDocument?.download_url ||
              // Use the backend download endpoint
              (documentDetails?.id &&
                `/api/main/download-document/?document_id=${documentDetails.id}`) ||
              (documentDetails?.id &&
                `/api/main/documents/${documentDetails.id}/download/`)

            if (fileUrl) {
              // Determine file type for proper display
              const fileName =
                selectedDocument?.name || documentDetails?.document_name || ''
              const fileExtension =
                fileName.split('.').pop()?.toLowerCase() ||
                documentDetails?.document_extension?.toLowerCase()
              const isImage = [
                'png',
                'jpg',
                'jpeg',
                'gif',
                'webp',
                'svg',
              ].includes(fileExtension)

              if (isImage) {
                return (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <img
                      src={fileUrl}
                      alt={fileName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '600px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                      onError={(e) => {
                        console.error('Modal image failed to load:', fileUrl)
                      }}
                    />
                  </div>
                )
              } else {
                return (
                  <iframe
                    src={fileUrl}
                    width="100%"
                    height="600px"
                    title="Document Viewer"
                    style={{ border: 'none' }}
                  />
                )
              }
            } else {
              return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Icon
                    name="file"
                    size="huge"
                    style={{ opacity: 0.3, marginBottom: '20px' }}
                  />
                  <Header as="h4">Document Preview Not Available</Header>
                  <p>This document cannot be previewed in the browser.</p>
                  <div style={{ marginTop: '20px' }}>
                    <strong>File:</strong>{' '}
                    {selectedDocument?.name ||
                      selectedDocument?.document_details?.document_name ||
                      'Unknown'}
                  </div>
                  <div>
                    <strong>Type:</strong>{' '}
                    {selectedDocument?.document_details?.document_extension ||
                      'Unknown'}
                  </div>
                  <div>
                    <strong>Uploaded:</strong>{' '}
                    {new Date(
                      selectedDocument?.created_at
                    ).toLocaleDateString()}
                  </div>
                  <Button
                    style={{ marginTop: '20px' }}
                    primary
                    icon="download"
                    content="Download File"
                    onClick={() => window.open(fileUrl || '#', '_blank')}
                    disabled={!fileUrl}
                  />
                </div>
              )
            }
          })()}
        </Modal.Content>
      </Modal>
    </div>
  )
}

export default TakeoffView
